import React, { useEffect, useState } from "react";
import { Box } from "./robotsStyle";
import { isInIframe } from "../../utils/is-in-iframe";
import { ToolsHeader } from "../components/ToolsHeader";

interface Rule {
  allowed: boolean;
  line: number;
  rule: string;
  url: string;
  details: string;
}

interface Recommendation {
  type: string;
  description: string;
  suggestion: string;
}

const isValidUrl = (str: string) => {
  try {
    new URL(str);
    return true;
  } catch {
    return false;
  }
};

export const RobotsValidator: React.FC = () => {
  const [url, setUrl] = useState("");
  const [rules, setRules] = useState<Rule[]>([]);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [resultLoading, setResultLoading] = useState(false);

  const [mode, setMode] = useState<"url" | "file" | "text">("url");
  const [textContent, setTextContent] = useState("");
  const [fileContent, setFileContent] = useState("");

  const [testUrl, setTestUrl] = useState("");
  const [testResult, setTestResult] = useState<"allowed" | "blocked" | "">("");

  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [rules, showResults, testResult, textContent]);

  useEffect(() => {
    if (mode === "text" && textContent.trim()) {
      const analysis = analyzeRobotsTxt(textContent, "pasted-text");
      setRules(analysis.rules);
      setRecommendations(analysis.recommendations);
      setShowResults(true);
    }
  }, [textContent, mode]);

  const cleanUrl = (raw: string) =>
    raw.replace(/[*?$()[\]{}^]/g, "").trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setResultLoading(true);

    let inputUrl = url.trim();
    if (!inputUrl.startsWith("http://") && !inputUrl.startsWith("https://")) {
      inputUrl = "https://" + inputUrl;
    }

    const robotsUrl = inputUrl.endsWith("robots.txt")
      ? inputUrl
      : new URL("robots.txt", inputUrl).href;

    try {
      const apiUrl = `https://api.performancedashboard.io/api/robots/fetch?url=${encodeURIComponent(
        robotsUrl
      )}`;

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: { Accept: "application/json" },
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || `HTTP ${response.status}`);
      }

      const body = await response.json();
      const content = body.content;

      if (!isValidRobotsTxt(content)) {
        throw new Error("Invalid robots.txt file format");
      }

      const analysis = analyzeRobotsTxt(content, robotsUrl);
      setRules(analysis.rules);
      setRecommendations(analysis.recommendations);
      setShowResults(true);
      setTestResult("");
    } catch (error: any) {
      alert(error.message);
      setShowResults(false);
    } finally {
      setResultLoading(false);
    }
  };

  const handleFileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setResultLoading(true);

    try {
      const response = await fetch("https://api.performancedashboard.io/api/robots/upload", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: fileContent }),
      });

      if (!response.ok) throw new Error("Failed to analyze file");

      const body = await response.json();
      const content = body.content;

      const analysis = analyzeRobotsTxt(content, "uploaded-file");
      setRules(analysis.rules);
      setRecommendations(analysis.recommendations);
      setShowResults(true);
      setTestResult("");
    } catch (error: any) {
      alert(error.message);
      setShowResults(false);
    } finally {
      setResultLoading(false);
    }
  };


  const fetchRobotsForPasteMode = async () => {
    if (!url.trim()) {
      alert("Enter valid robots.txt URL");
      return;
    }

    let robotsUrl = url.trim();
    if (!robotsUrl.startsWith("http://") && !robotsUrl.startsWith("https://")) {
      robotsUrl = "https://" + robotsUrl;
    }
    if (!robotsUrl.endsWith("robots.txt")) {
      robotsUrl = new URL("robots.txt", robotsUrl).href;
    }

    try {
      setResultLoading(true);

      const response = await fetch(
        `https://api.performancedashboard.io/api/robots/fetch?url=${encodeURIComponent(
          robotsUrl
        )}`
      );

      if (!response.ok) throw new Error("Failed to fetch robots.txt");

      const body = await response.json();
      const content = body.content;

      setTextContent(content);

      const analysis = analyzeRobotsTxt(content, robotsUrl);
      setRules(analysis.rules);
      setRecommendations(analysis.recommendations);
      setShowResults(true);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setResultLoading(false);
    }
  };

  const isValidRobotsTxt = (content: string): boolean => {
    const valid = [
      "user-agent:",
      "allow:",
      "disallow:",
      "sitemap:",
      "crawl-delay:",
    ];
    return content
      .toLowerCase()
      .split("\n")
      .some((line) => valid.some((d) => line.trim().startsWith(d)));
  };

  const evaluateUrlAgainstRobots = (
    robotsContent: string,
    urlToTest: string
  ): "allowed" | "blocked" => {
    const lines = robotsContent.split("\n");

    let currentAgent = "*";
    const allowRules: string[] = [];
    const disallowRules: string[] = [];

    lines.forEach((line) => {
      const trimmed = line.trim().toLowerCase();
      if (!trimmed) return;

      if (trimmed.startsWith("user-agent:")) {
        currentAgent = trimmed.replace("user-agent:", "").trim();
      }

      if (currentAgent !== "*") return;

      if (trimmed.startsWith("allow:"))
        allowRules.push(trimmed.replace("allow:", "").trim());

      if (trimmed.startsWith("disallow:"))
        disallowRules.push(trimmed.replace("disallow:", "").trim());
    });

    let matchedAllow = "";
    let matchedDisallow = "";

    const checkMatch = (pattern: string): boolean => {
      if (!pattern) return false;
      const regex = pattern.replace(/\*/g, ".*").replace(/\?/g, ".");
      return new RegExp(regex).test(urlToTest);
    };

    allowRules.forEach((rule) => {
      if (checkMatch(rule) && rule.length > matchedAllow.length)
        matchedAllow = rule;
    });

    disallowRules.forEach((rule) => {
      if (checkMatch(rule) && rule.length > matchedDisallow.length)
        matchedDisallow = rule;
    });

    if (matchedAllow.length > matchedDisallow.length) return "allowed";
    if (matchedDisallow.length > matchedAllow.length) return "blocked";
    return "allowed";
  };

  useEffect(() => {
    if (!testUrl.trim() || !textContent.trim()) {
      setTestResult("");
      return;
    }

    const cleaned = cleanUrl(testUrl);
    const result = evaluateUrlAgainstRobots(textContent, cleaned);
    setTestResult(result);
  }, [testUrl, textContent]);

  const analyzeRobotsTxt = (content: string, url: string) => {
    const lines = content.split("\n");
    const rules: Rule[] = [];
    const recommendations: Recommendation[] = [];

    let currentUserAgent = "*";
    let hasWildcardUserAgent = false;
    let hasSitemap = false;
    let hasCrawlDelay = false;
    let hasComments = false;
    let consecutiveEmptyLines = 0;

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();

      if (!trimmedLine) {
        consecutiveEmptyLines++;
        return;
      } else {
        consecutiveEmptyLines = 0;
      }

      if (trimmedLine.toLowerCase().startsWith("sitemap")) {
        hasSitemap = true;
      }

      if (trimmedLine.startsWith("#")) {
        hasComments = true;
      }

      const [directive, ...value] = trimmedLine
        .trim()
        .split(":")
        .map((part) => part.trim());

      const valueStr = value.join(":");
      const directiveLower = directive.toLowerCase();

      if (directiveLower === "user-agent") {
        currentUserAgent = valueStr;
        if (valueStr === "*") hasWildcardUserAgent = true;
      } else if (directiveLower === "crawl-delay") {
        hasCrawlDelay = true;
      } else if (["allow", "disallow"].includes(directiveLower)) {
        rules.push({
          allowed: directiveLower === "allow",
          line: index + 1,
          rule: trimmedLine,
          url,
          details: `User-agent: ${currentUserAgent}\n${trimmedLine}`,
        });
      }
    });


    if (!hasSitemap) {
      recommendations.push({
        type: "Add Sitemap Directive",
        description: "Adding a Sitemap directive helps search engines.",
        suggestion: `Sitemap: ${
          isValidUrl(url)
            ? new URL("sitemap.xml", url).href
            : "https://example.com/sitemap.xml"
        }`,
      });
    }

    if (!hasWildcardUserAgent) {
      recommendations.push({
        type: "Add Wildcard User-Agent",
        description: "Include a wildcard (*) User-agent section.",
        suggestion: "User-agent: *\nDisallow: /private/\nDisallow: /admin/",
      });
    }

    const disallowRules = rules.filter((r) => !r.allowed);
    if (disallowRules.length > 3) {
      recommendations.push({
        type: "Optimize Crawl Rules",
        description:
          "Consider consolidating similar Disallow rules for simplicity.",
        suggestion: "Use wildcards.\nExample:\nDisallow: /private/*",
      });
    }

    if (!hasCrawlDelay && rules.length > 5) {
      recommendations.push({
        type: "Consider Adding Crawl-delay",
        description: "Useful for large websites.",
        suggestion: "Crawl-delay: 1",
      });
    }

    if (!hasComments) {
      recommendations.push({
        type: "Add Comments",
        description:
          "Adding comments helps explain the intent behind your rules.",
        suggestion:
          "# Block admin area\nDisallow: /admin/\n\n# Block private files",
      });
    }

    if (consecutiveEmptyLines > 1) {
      recommendations.push({
        type: "Remove Extra Blank Lines",
        description: "Cleanup improves readability.",
        suggestion: "Remove unnecessary blank lines.",
      });
    }

    return { rules, recommendations };
  };

  return (
    <Box>
      <div
        className={`container_robots ${
          !isInIframe ? "py-5 defaultText" : "py-1 interDisplay"
        }`}
      >
        {!isInIframe && (
          <ToolsHeader
            title="Robots.txt Testing & Validation Tool"
            description="Validate and test your robots.txt file."
          />
        )}

        <main>
          <div className="mode-switch">
            <button
              type="button"
              className={`toggle-btn ${mode === "url" ? "active" : ""}`}
              onClick={() => setMode("url")}
            >
              WITH URL
            </button>
            <button
              type="button"
              className={`toggle-btn ${mode === "file" ? "active" : ""}`}
              onClick={() => setMode("file")}
            >
              WITH FILE
            </button>
            <button
              type="button"
              className={`toggle-btn ${mode === "text" ? "active" : ""}`}
              onClick={() => setMode("text")}
            >
              PASTE AS TEXT
            </button>
          </div>

          <div className="input-section">
            {mode === "url" && (
              <form onSubmit={handleSubmit}>
                <label>Enter Website URL</label>
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="example.com"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                  />
                  <button
                    type="submit"
                    className="but_imp"
                    style={{ backgroundColor: "#8cc540" }}
                  >
                    {resultLoading ? "fetching..." : "Fetch & Analyze"}
                  </button>
                </div>
              </form>
            )}

            {mode === "file" && (
              <form onSubmit={handleFileSubmit}>
                <label>Upload robots.txt file</label>
                <input
                  type="file"
                  accept=".txt"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      const reader = new FileReader();
                      reader.onload = (ev) =>
                        setFileContent(ev.target?.result as string);
                      reader.readAsText(f);
                    }
                  }}
                />

                <button
                  type="submit"
                  className="but_imp"
                  style={{ backgroundColor: "#8cc540", marginTop: "10px" }}
                >
                  {resultLoading ? "processing..." : "Analyze File"}
                </button>
              </form>
            )}

            {mode === "text" && (
              <div>
                <label>Robots.txt URL</label>
                <div style={{ display: "flex", gap: "10px" }}>
                  <input
                    type="text"
                    placeholder="https://example.com/robots.txt"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    style={{
                      flex: 1,
                      padding: "10px",
                      border: "1px solid #ccc",
                      borderRadius: "5px",
                      marginBottom: "15px",
                    }}
                  />

                  <button
                    type="button"
                    onClick={fetchRobotsForPasteMode}
                    className="but_imp"
                    style={{
                      backgroundColor: "#8cc540",
                      height: "40px",
                      marginBottom: "15px",
                    }}
                  >
                    {resultLoading ? "Fetching..." : "Fetch robots.txt"}
                  </button>
                </div>

                <label>Paste or write your robots.txt file code:</label>
                <textarea
                  rows={10}
                  value={textContent}
                  onChange={(e) => setTextContent(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ccc",
                    borderRadius: "5px",
                    marginBottom: "15px",
                    fontFamily: "monospace",
                  }}
                />

                <label>Enter the URL to test</label>
                <input
                  type="text"
                  placeholder="https://example.com/page.htm"
                  value={testUrl}
                  onChange={(e) => setTestUrl(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ccc",
                    borderRadius: "5px",
                    marginBottom: "15px",
                  }}
                />

                {testResult && (
                  <div
                    style={{
                      width: "100%",
                      padding: "10px",
                      backgroundColor:
                        testResult === "allowed" ? "#4CAF50" : "#F44336",
                      color: "white",
                      fontWeight: "bold",
                      borderRadius: "5px",
                      marginBottom: "15px",
                      textAlign: "center",
                    }}
                  >
                    {testResult === "allowed" ? "Allowed" : "Blocked"}
                  </div>
                )}
              </div>
            )}
          </div>

          {showResults && (
            <>
              <div className="results-section">
                <table>
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Line / Rule</th>
                      <th>URL</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules.map((rule, i) => (
                      <tr key={i}>
                        <td>
                          <span
                            className={`status-badge ${
                              rule.allowed ? "status-allowed" : "status-blocked"
                            }`}
                          >
                            {rule.allowed ? "Allowed" : "Blocked"}
                          </span>
                        </td>
                        <td>
                          <div>Line {rule.line}</div>
                          <div style={{ color: "gray" }}>{rule.rule}</div>
                        </td>
                        <td>{rule.url}</td>
                        <td>
                          <pre style={{ whiteSpace: "pre-wrap" }}>
                            {rule.details}
                          </pre>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="recommendations-section">
                <h2>Recommendations</h2>
                <div id="recommendationsList">
                  {recommendations.map((rec, i) => (
                    <div className="recommendation-card" key={i}>
                      <div className="recommendation-title">{rec.type}</div>
                      <div className="recommendation-description">
                        {rec.description}
                      </div>
                      <div className="recommendation-code">
                        <pre>{rec.suggestion}</pre>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </Box>
  );
};

export default RobotsValidator;
